import React, { useMemo } from 'react';

// Components
import AccountSetRow, { UNSELECTED_ITEM_INDEX } from '@/views/components/modals/account-sets/AccountSetRow';
import CollapsibleContainer from '@/views/components/misc/CollapsibleContainer';
import EditableInput from '@/views/components/inputs/EditableInput';
import HoldingIcon from '@/views/components/common/HoldingIcon';
import IconButton from '@/views/components/buttons/IconButton';
import { SecondaryLink } from '@/views/components/buttons';
import { SelectItem } from '@/views/components/inputs/Select';

// Enums
import { DEFAULT_OPTIONAL_RECOMMENDATION_COUNT, AccountSetRowKind } from '@/enums/AccountSets';

// Types
import AccountSet, { AccountSetRecommendation } from '@/types/comparison-account-set';
import ComparisonItem from '@/types/comparison-item';
import LocalizedString from '@/types/localized-string';

// Utils
import { formatLocalizedString, formatMessage, formatMessageWithLocale, LOCALE } from '@/utils/i18n';

type Props = {
  accountSet: AccountSet;
  comparisonItems: ComparisonItem[];
  optionalRecommendationLabels?: LocalizedString[];
  getSelectItemsForRow?: (params: { rowKind: AccountSetRowKind; selectedItemIndex: number; accountSetId: string }) => SelectItem[];
  onChange: (nextAccountSet: AccountSet) => void;
  onDelete: () => void;
  defaultExpanded?: boolean;
  validation?: {
    currentMissing: boolean;
    primaryRecommendedMissing: boolean;
  };
  forceExpandNonce?: number;
};

const PLACEHOLDER_RECOMMENDATION_INDEX = -1;

const buildDefaultRecommendationLabel = (isPrimary: boolean): LocalizedString => {
  const key = isPrimary ? 'Comparison.AccountSets.Row.Recommended' : 'Comparison.AccountSets.Row.RecommendedOptional';
  return {
    [LOCALE.EN_CA]: formatMessageWithLocale(key, LOCALE.EN_CA),
    [LOCALE.FR_CA]: formatMessageWithLocale(key, LOCALE.FR_CA),
  };
};

const buildPlaceholderRecommendation = () => ({
  rec: {
    label: buildDefaultRecommendationLabel(true),
    itemId: UNSELECTED_ITEM_INDEX,
    isPrimary: true,
  },
  originalIndex: PLACEHOLDER_RECOMMENDATION_INDEX,
});

const getSortedRecommendationsWithIndex = (recommendations: AccountSetRecommendation[]) =>
  recommendations.map((rec, originalIndex) => ({ rec, originalIndex })).sort((a, b) => Number(b.rec.isPrimary) - Number(a.rec.isPrimary));

const AccountSetItem: React.FC<Props> = props => {
  const {
    accountSet,
    comparisonItems,
    optionalRecommendationLabels,
    getSelectItemsForRow,
    onChange,
    onDelete,
    defaultExpanded = true,
    validation,
    forceExpandNonce,
  } = props;
  const { _id: accountSetId, name } = accountSet;

  const defaultSelectItems = useMemo(
    () =>
      comparisonItems.map((item, index) => ({
        value: String(index),
        label: (
          <span className="account-set-row-select-option">
            <span className="account-set-row-select-option-icon" aria-hidden>
              <HoldingIcon type={item.type} color={item.color} size={14} />
            </span>
            <span data-private data-dd-privacy="mask">
              {formatLocalizedString(item.name)}
            </span>
          </span>
        ),
      })),
    [comparisonItems],
  );

  const getRowSelectItems = (rowKind: AccountSetRowKind, selectedItemIndex: number) => {
    if (!getSelectItemsForRow) return defaultSelectItems;
    return getSelectItemsForRow({ rowKind, selectedItemIndex, accountSetId });
  };

  const updateCurrent = (nextIndex: number) => {
    onChange({ ...accountSet, current: nextIndex });
  };

  const clearCurrent = () => {
    onChange({ ...accountSet, current: UNSELECTED_ITEM_INDEX });
  };

  const updateRecommendation = (targetIdx: number, nextItemId: number, baseRecommendation?: AccountSetRecommendation) => {
    if (targetIdx === PLACEHOLDER_RECOMMENDATION_INDEX) {
      const isPrimary = baseRecommendation?.isPrimary ?? true;
      const baseLabel = baseRecommendation?.label;
      const hasBaseLabel = baseLabel != null && Object.keys(baseLabel).length > 0;

      const nextRec: AccountSetRecommendation = {
        label: hasBaseLabel ? baseLabel : buildDefaultRecommendationLabel(isPrimary),
        isPrimary,
        itemId: nextItemId,
      };
      onChange({ ...accountSet, recommendations: [nextRec] });
      return;
    }

    const nextRecs = accountSet.recommendations.map((rec, idx) => (idx === targetIdx ? { ...rec, itemId: nextItemId } : rec));
    onChange({ ...accountSet, recommendations: nextRecs });
  };

  const clearRecommendation = (targetIdx: number, baseRecommendation?: AccountSetRecommendation) => {
    updateRecommendation(targetIdx, UNSELECTED_ITEM_INDEX, baseRecommendation);
  };

  const removeRecommendation = (targetIdx: number) => {
    if (targetIdx === PLACEHOLDER_RECOMMENDATION_INDEX) return;
    const target = accountSet.recommendations[targetIdx];
    if (!target || target.isPrimary) return;

    const nextRecs = accountSet.recommendations.filter((_, idx) => idx !== targetIdx);
    onChange({ ...accountSet, recommendations: nextRecs });
  };

  const optionalRecommendationsCount = useMemo(() => accountSet.recommendations.filter(r => !r.isPrimary).length, [accountSet.recommendations]);
  const canAddOptionalRecommendation = optionalRecommendationsCount < DEFAULT_OPTIONAL_RECOMMENDATION_COUNT;

  const addOptionalRecommendation = () => {
    if (!canAddOptionalRecommendation) return;

    const hasPrimary = accountSet.recommendations.some(r => r.isPrimary);
    let baseRecommendations: AccountSetRecommendation[] = accountSet.recommendations;

    if (!hasPrimary) {
      baseRecommendations = [
        {
          label: buildDefaultRecommendationLabel(true),
          itemId: UNSELECTED_ITEM_INDEX,
          isPrimary: true,
        },
        ...accountSet.recommendations,
      ];
    }

    const configuredOptionalLabel = optionalRecommendationLabels?.[optionalRecommendationsCount];

    const nextRecs: AccountSetRecommendation[] = [
      ...baseRecommendations,
      {
        label: configuredOptionalLabel ?? buildDefaultRecommendationLabel(false),
        itemId: UNSELECTED_ITEM_INDEX,
        isPrimary: false,
      },
    ];

    onChange({ ...accountSet, recommendations: nextRecs });
  };

  const sortedRecommendations = useMemo(() => getSortedRecommendationsWithIndex(accountSet.recommendations), [accountSet.recommendations]);

  const recommendationsToRender = sortedRecommendations.length > 0 ? sortedRecommendations : [buildPlaceholderRecommendation()];

  const renderRecommendationRows = () => {
    let optionalLabelIndex = 0;

    return recommendationsToRender.map(({ rec, originalIndex }, idx) => {
      const localized = formatLocalizedString(rec.label);

      const configuredOptionalLabel =
        !rec.isPrimary && optionalRecommendationLabels?.[optionalLabelIndex] ? formatLocalizedString(optionalRecommendationLabels[optionalLabelIndex]!) : '';

      if (!rec.isPrimary) optionalLabelIndex += 1;

      const label =
        localized ||
        configuredOptionalLabel ||
        formatMessage(rec.isPrimary ? 'Comparison.AccountSets.Row.Recommended' : 'Comparison.AccountSets.Row.RecommendedOptional');

      const iconToUse = rec.isPrimary ? 'icon-ds-radio-button-selected' : 'icon-ds-radio-button-partial';

      const key = originalIndex === PLACEHOLDER_RECOMMENDATION_INDEX ? `${accountSetId}-rec-placeholder` : `${accountSetId}-rec-${originalIndex}`;

      return (
        <AccountSetRow
          key={key}
          data-testid={`accountSetItem-row-recommendation-${idx}`}
          label={label}
          labelForA11y={label}
          icon={iconToUse}
          items={getRowSelectItems(rec.isPrimary ? AccountSetRowKind.RECOMMENDED : AccountSetRowKind.RECOMMENDED_OPTIONAL, rec.itemId ?? UNSELECTED_ITEM_INDEX)}
          selectedItemIndex={rec.itemId ?? UNSELECTED_ITEM_INDEX}
          onChange={nextIndex => updateRecommendation(originalIndex, nextIndex, rec)}
          onClear={() => clearRecommendation(originalIndex, rec)}
          onRemove={!rec.isPrimary ? () => removeRecommendation(originalIndex) : undefined}
          hasError={rec.isPrimary ? validation?.primaryRecommendedMissing : false}
        />
      );
    });
  };

  const currentLabel = formatMessage('Comparison.AccountSets.Row.Current');

  const updateName = (nextName: string) => {
    onChange({ ...accountSet, name: nextName });
  };

  const headerContent = (
    <div className="account-set-item-name" role="presentation">
      <EditableInput
        className="editable-input-v2 account-set-item-editable-name"
        data-private
        data-dd-privacy="mask"
        value={name}
        onValueChange={updateName}
        placeholder={formatMessage('Comparison.AccountSets.Item.Rename')}
        tooltipConfig={{
          show: true,
          message: formatMessage('Comparison.AccountSets.Item.Rename'),
        }}
      />
    </div>
  );

  const headerRightContent = (
    <div className="account-set-item-header-actions">
      {validation && (validation.currentMissing || validation.primaryRecommendedMissing) && (
        <span className="account-set-item-error-pill" data-testid="accountSetItem-errorPill">
          <i className="icon-ds-error" aria-hidden />
          <span>{formatMessage('Comparison.AccountSets.Validation.MissingPortfolio')}</span>
        </span>
      )}

      <IconButton
        className="icon-button-v2"
        icon="ds-delete"
        onClick={() => onDelete()}
        tooltip={formatMessage('Comparison.AccountSets.Item.Delete')}
        tooltipClassName="custom-tooltip-v2"
        stopClickPropagation
        ariaLabel={formatMessage('Comparison.AccountSets.Item.Delete')}
      />
    </div>
  );

  return (
    <div className="account-set-item">
      <CollapsibleContainer
        className="account-set-item-collapsible"
        key={`${accountSetId}-${forceExpandNonce ?? 0}`}
        headerText={name}
        headerContent={headerContent}
        headerRightContent={headerRightContent}
        allowInteractiveHeader
        getToggleAriaLabel={isOpen => `${formatMessage(isOpen ? 'CollapseToggle.Collapse' : 'CollapseToggle.Expand')} ${name}`}
        defaultOpen={defaultExpanded ?? true}
        headerTag="div"
      >
        <div className="account-set-item-body">
          <div className="account-set-item-rows">
            <AccountSetRow
              data-testid="accountSetItem-row-current"
              label={currentLabel}
              labelForA11y={currentLabel}
              icon="icon-ds-star"
              items={getRowSelectItems(AccountSetRowKind.CURRENT, accountSet.current ?? UNSELECTED_ITEM_INDEX)}
              selectedItemIndex={accountSet.current ?? UNSELECTED_ITEM_INDEX}
              onChange={updateCurrent}
              onClear={clearCurrent}
              hasError={validation?.currentMissing}
            />

            {renderRecommendationRows()}

            {canAddOptionalRecommendation && (
              <SecondaryLink className="secondary-v2 account-set-item-add-optional" compact icon="ds-add" onClick={addOptionalRecommendation}>
                {formatMessage('Comparison.AccountSets.Row.AddOptionalRecommendation')}
              </SecondaryLink>
            )}
          </div>
        </div>
      </CollapsibleContainer>
    </div>
  );
};

export default AccountSetItem;
